import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody, Col, Row, FormGroup, Label } from 'reactstrap'
import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import { Search, ChevronRight, Calendar } from 'react-feather'
import Flatpickr from 'react-flatpickr'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment-timezone'
import { useLazyFetchConfigurationTraceabilityQuery } from './dashboardTraceabilityApiSlice'
import { URL_DASHBOARD_TRACEABILITY } from 'utils/pathUrl'

const monthsLabel = [
  { value: '1', label: 'Januari' },
  { value: '2', label: 'Februari' },
  { value: '3', label: 'Maret' },
  { value: '4', label: 'April' },
  { value: '5', label: 'Mei' },
  { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' },
  { value: '8', label: 'Agustus' },
  { value: '9', label: 'September' },
  { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' },
  { value: '12', label: 'Desember' },
]

const DashboardTraceability = () => {
  const navigate = useNavigate()
  const [selectedMonthYear, setSelectedMonthYear] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [configData, setConfigData] = useState(null)

  const [fetchConfig, { isLoading, isFetching, isError }] = useLazyFetchConfigurationTraceabilityQuery()

  const handleChangeMonthYear = (selectedDates) => {
    if (selectedDates.length > 0) {
      const date = selectedDates[0]
      setSelectedMonthYear(date)
      setHasSearched(true)
      
      const month = moment(date).format('M')
      const year = moment(date).format('YYYY')

      fetchConfig({ month, year })
        .unwrap()
        .then((res) => {
          setConfigData(res)
        })
        .catch(() => {
          setConfigData(null)
        })
    } else {
      setSelectedMonthYear('')
      setHasSearched(false)
      setConfigData(null)
    }
  }

  const hasResults = !!configData

  const handleGoToDetail = () => {
    if (configData && configData.id) {
      navigate(`${URL_DASHBOARD_TRACEABILITY}/${configData.id}`, { state: { configData } })
    }
  }

  const renderPeriodText = () => {
    if (!selectedMonthYear) return ''
    const month = moment(selectedMonthYear).format('M')
    const year = moment(selectedMonthYear).format('YYYY')
    const monthStr = monthsLabel.find(m => m.value === month)?.label || month
    return `${monthStr} ${year}`
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Dashboard Ketelusuran'} />
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <h5 className="fw-semibold mb-1">Dashboard Ketelusuran</h5>
            <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
              Pilih bulan dan tahun untuk melihat data ketelusuran.
            </p>

            {/* Select Month & Year (Flatpickr) */}
            <Row className="mb-2">
              <Col xs="12" sm="6" md="4">
                <FormGroup>
                  <Label>Bulan</Label>
                  <div
                    className="d-flex align-items-center border rounded"
                    style={{ height: 38 }}
                  >
                    <Calendar size={17} className="ms-3 text-muted" />
                    <Flatpickr
                      value={
                        selectedMonthYear
                          ? moment(selectedMonthYear).format('YYYY-MM')
                          : ''
                      }
                      onChange={handleChangeMonthYear}
                      data-testid="trace-month-picker"
                      options={{
                        altInput: true,
                        altFormat: 'F Y',
                        dateFormat: 'Y-m',
                        defaultDate: selectedMonthYear
                          ? moment(selectedMonthYear).format('YYYY-MM')
                          : undefined,
                        maxDate: moment().format('YYYY-MM'),
                        disableMobile: true,
                        plugins: [
                          new MonthSelectPlugin({
                            shorthand: false,
                            dateFormat: 'Y-m',
                            altFormat: 'F Y',
                          }),
                        ],
                      }}
                      className="form-control bg-transparent border-0 shadow-none px-2"
                      placeholder="Pilih Bulan & Tahun"
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* Loading Config */}
            {(isLoading || isFetching) && (
              <div className="d-flex justify-content-center py-4">
                <LoadingAnimation />
              </div>
            )}

            {/* Initial state - No Search */}
            {!hasSearched && !isLoading && !isFetching && (
              <div className="text-center py-5">
                <Search size={40} className="text-muted mb-3" />
                <p className="text-muted mb-0">
                  Pilih bulan dan tahun lalu klik Cari untuk melihat informasi ketelusuran.
                </p>
              </div>
            )}

            {/* No Result */}
            {hasSearched && !isLoading && !isFetching && !hasResults && !isError && (
              <div className="text-center py-5">
                <Search size={40} className="text-muted mb-3" />
                <p className="fw-semibold mb-1">Data tidak ditemukan</p>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Tidak ada konfigurasi ketelusuran untuk periode bulan {renderPeriodText()}.
                </p>
              </div>
            )}

            {/* Error state */}
            {isError && (
              <div className="text-center py-5">
                <p className="fw-semibold text-danger mb-1">Terjadi Kesalahan</p>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Gagal mendapatkan data. Silakan periksa koneksi Anda dan coba lagi.
                </p>
              </div>
            )}

            {/* Results */}
            {hasSearched && hasResults && !isLoading && !isFetching && (
              <>
                <hr className="my-4" />
                
                <Card className="shadow-sm border" data-testid="trace-result-card">
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="fw-semibold mb-1">
                        Data Ketelusuran Bulan {renderPeriodText()}
                      </h6>
                      <p className="text-muted mb-0 text-sm">
                        ID: {configData.id}
                      </p>
                    </div>
                    <CustomButton color="primary" onClick={handleGoToDetail} testId="trace-detail-button">
                      Lihat Detail <ChevronRight size={16} className="ms-1" />
                    </CustomButton>
                  </CardBody>
                </Card>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default DashboardTraceability
